import { computed, defineComponent, reactive, ref, type CSSProperties, type Ref } from 'vue'
import type { ColorPickerProps } from './types'
import { FORM_INPUT_FIELD_PROPS } from '../form/input/types'
import type { IBaseComponentProps } from '../base/types'
import { GridColumn, GridRow } from '../grid'
import { useInterpolation, type InterpolationRunner } from '@/utils/animation/useInterpolation'
import { onClickOutside, useElementBounding } from '@vueuse/core'
import { range } from '@/utils/array'
import { hashu32 } from '@/utils/hash'
import { VEC3, Vector } from '@/utils/math/vector'
import { clamp, lerp } from '@/utils/math'
import { useEffect } from '@/utils/reactive/useEffect'
import { hashu32f } from 'math'

type ColorPickerState = {
  expanded: boolean
}


type PickerOption = {
  color: Vector;
  value?: Vector;
  label?: string;
}

type PickerPopupProps = IBaseComponentProps & {
  anchor: Ref
  animation: InterpolationRunner
  items: PickerOption[];
  selectedIndex: number
  handleSelect: (index: number) => void
}

type ColorItemProps = IBaseComponentProps & {
  selected: boolean;
  item: PickerOption;
}

const ColorItem = defineComponent<ColorItemProps>({
  setup(props) {

    const color = computed(() => props.item.value || VEC3(1, 1, 1));

    return () => {
      return (
        <div
          class="w-[42px] h-[42px] rounded-sm cursor-pointer transition-all flex items-center content-center justify-center"
          style={{
            background: color.value.scale(255).toRGB(4),
            ...(props.selected
              ? {
                  boxShadow: `0 2px 4px 0 rgba(0, 0, 0, 0.9)`
                }
              : {
                  boxShadow: `inset 0 2px 4px 0 rgba(0, 0, 0, 0.5)`
                })
          }}
        >
          { props.item.label && <div class='text-gray-900 text-[0.5rem] font-medium'>{props.item.label}</div> }
        </div>
      )
    }
  }
})

ColorItem.props = ['selected', 'item']

const PickerPopup = defineComponent<PickerPopupProps>({
  setup(props) {
    const self = ref()
    // const selfSize = useElementSize(self)
    // const selfBounding = useElementBounding(self)
    //const anchorSize = useElementSize(props.anchor)
    const anchorBounding = useElementBounding(props.anchor)

    const positionStyle = computed((): CSSProperties => {
      const x = anchorBounding.x
      const y = anchorBounding.y

      return {
        top: y + 'px',
        left: x + 'px'
      }
    })

    const animatedStyle = computed((): CSSProperties => {
      const s = props.animation.state.value

      const opacity = s * 100

      const scale = lerp(0.5, 1.0, s)

      return {
        opacity: `${opacity}%`,
        transform: `scale(${scale}, ${scale})`,
        ...(s <= 0.00001 ? {
          pointerEvents: 'none',
          display: 'none'
        } : {})
      }
    })

    const handleClick = (index: number) => {
      props.handleSelect(index)
    }

    return () => {
      return (
        <div
          ref={self}
          class="bg-white rounded-sm shadow-lg p-4 pretty-scrollbar"
          style={[
            {
              minWidth: '20rem',
              maxHeight: '20rem',
              overflowY: 'auto',
              position: 'fixed'
            },
            animatedStyle.value,
            positionStyle.value
          ]}
        >
          <GridRow
            justifyContent="space-between"
            justifyItems="center"
            alignItems="center"
            layout="unset"
            areas='"a b c d" "e f g h"'
            style={{
              gridTemplateColumns: 'repeat(auto-fit, minmax(3rem, 1fr))',
              gridAutoRows: '3rem'
            }}
          >
            {props.items.map((it, i) => {
              return (
                <ColorItem
                  key={`color-${i}`}
                  onClick={() => handleClick(i)}
                  selected={i === props.selectedIndex}
                  item={it}
                />
              )
            })}
          </GridRow>
        </div>
      )
    }
  }
})

PickerPopup.props = ['anchor', 'animation', 'items', 'selectedIndex', 'handleSelect']

const generateColors = (count: number): Vector[] => {
  let seed: number = 41923

  return range(count).map((_i) => {
    const r = hashu32f(seed)
    seed += hashu32(seed + r)
    const g = hashu32f(seed)
    seed += hashu32(seed + g)
    const b = hashu32f(seed)
    seed += hashu32(seed + b)
    return VEC3(r, g, b)
  })
}

const ColorPicker = defineComponent<ColorPickerProps>({
  setup(props) {
    const state = reactive<ColorPickerState>({
      expanded: false
    })

    const colors = generateColors(64)
    const stringColors = ['', ...colors.map((col) => col.scale(255).toRGB(4))]

    useEffect(() => {
      console.log({ value: props.value })
    }, [() => props.value])
    const items = [
      {
        color: VEC3(1, 1, 1),
        value: undefined,
        label: 'None'
      },
      ...colors.map((color): PickerOption => {
        return {
          color: color,
          value: color,
        }
      })
    ];
    const animation = useInterpolation({
      initial: 0,
      duration: 0.15
    })

    const setExpanded = (expanded: boolean) => {
      state.expanded = expanded

      if (expanded) {
        animation.runTo(1.0)
      } else {
        animation.runTo(0.0)
      }
    }

    const toggleExpanded = () => {
      setExpanded(!state.expanded)
    }

    const anchor = ref()

    const selectedIndex = computed(() => {
      return clamp(stringColors.indexOf(props.value), 0, colors.length - 1)
    })

    const handleClickColor = (index: number): void => {
      const rgb = stringColors[index]
      const fieldRef = props.fieldRef
      if (fieldRef && props.reportChange) {
        props.reportChange(fieldRef, index <= 0 ? undefined : rgb, index <= 0)
      }
    }

    const popupRef = ref()

    onClickOutside(
      popupRef,
      () => {
        setExpanded(false)
      },
      { ignore: [anchor] }
    )

    return () => {
      return (
        <div>
          <GridRow gap="1rem" layout="min-content">
            <GridColumn ref={anchor} alignItems="center" justifyItems="center">
              <ColorItem
                onClick={toggleExpanded}
                selected={false}
                item={items[selectedIndex.value]}
              />
            </GridColumn>
          </GridRow>
          <PickerPopup
            ref={popupRef}
            handleSelect={handleClickColor}
            items={items}
            anchor={anchor}
            animation={animation}
            selectedIndex={selectedIndex.value}
          />
        </div>
      )
    }
  }
})

ColorPicker.props = FORM_INPUT_FIELD_PROPS

export default ColorPicker
